import { FC, ReactElement } from 'react';
import { Redirect } from 'react-router-dom';
import { ROUTES } from 'config';
import { Loader } from 'melp-design/components';
import { AdminPermission } from 'store/admin-admins';
import { containsAll } from 'utils/general';
import { AdminRole } from 'store/admins';
import { PermissionName } from 'store/permissions';
import { useAuthContext } from '../../auth/index';
import {
  GlobalPermissionModules,
  LocalPermissionModules,
  PermissionLevels,
} from '../../constants/Administrators';
import { useMe } from '../../state/Administrators';

interface Props {
  /**
   * Route path
   */
  path: string;
  /**
   * Route content
   */
  children: ReactElement;
  /**
   * Roles by which a route is accessible
   */
  roles?: AdminRole[];
  /**
   * Modules, which a user has to have access to, in order to access a route.
   * Legacy permissions; used when the `advancedPermissions` feature is off.
   */
  module?:
    | LocalPermissionModules
    | GlobalPermissionModules
    | Array<LocalPermissionModules | GlobalPermissionModules>;
  adminModules?: AdminPermission[];
  /**
   * Permission level, which a user must have, to access a route.
   * Legacy permissions; used when the `advancedPermissions` feature is off.
   */
  permissionLevel?: PermissionLevels;
  /**
   * Permissions (v2), at least one of which a user must have with
   * `accessLevel > 0` to access a route,
   */
  permission?: readonly PermissionName[];
}

export const ProtectedRoute: FC<Props> = ({
  roles,
  permissionLevel,
  module,
  adminModules = [],
  permission,
  path,
  children,
}) => {
  const { loggedIn } = useAuthContext();

  const {
    me,
    loading,
    getPermissionsByModule,
    adminPermissions,
    isRoot,
    isFeatureEnabled,
    hasViewPermission,
  } = useMe();

  if (!loggedIn) {
    return (
      <Redirect to={{ pathname: ROUTES.auth.signin, state: { from: path } }} />
    );
  }

  if (loading) return <Loader />;

  const hasRequiredRole = !!me?.role && !!roles && roles.includes(me.role);

  const hasRequiredPermissions = () => {
    if (me?.role === 'melpAdmin') {
      return containsAll(adminPermissions, adminModules);
    }

    if (isRoot) return true;

    if (isFeatureEnabled('advancedPermissions')) {
      return hasViewPermission(permission);
    }

    if (!permissionLevel) return true;

    const requiredPermissions = module ? getPermissionsByModule(module) : [];

    return requiredPermissions.some(
      (permission) =>
        permission.permission === 'edit' || permission.permission === 'view',
    );
  };

  if (!hasRequiredRole || !hasRequiredPermissions()) {
    return <Redirect to={ROUTES.root.notFound} />;
  }

  return children;
};
